import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { createThread } from '../../utils/api';
import Tests from '../shorts/Tests';
import News from '../shorts/News';
import { EditorState, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'; // Styles für den WYSIWYG-Editor

import { bold, italic, underline, strikethrough, monospace, superscript, subscript, unordered, ordered, indent, outdent, left, center, right, justify, color, link, emoji, embedded, image, eraser, undo, redo, fontSize, unlink } from 'react-draft-wysiwyg'; // Icons importieren

// Importiere die Funktion zum Umwandeln von Draft.js Content in HTML
import { stateToHTML } from 'draft-js-export-html';

import '../../assets/css/create-thread.css';

const CreateThread = () => {
    const [searchParams] = useSearchParams();
    const categoryId = searchParams.get('categoryId');
    const categoryName = searchParams.get('categoryName');
    const navigate = useNavigate();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [editorState, setEditorState] = useState(EditorState.createEmpty()); // Editor-Initialisierung
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    // Hier sollte die userId gesetzt werden – anpassen je nach Login-System
    const userId = localStorage.getItem('userId');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title || !editorState.getCurrentContent().hasText() || !categoryId) {
            setError("Bitte alle Felder ausfüllen.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Konvertiere den Editor-Inhalt in HTML
            const contentState = editorState.getCurrentContent();
            const htmlContent = stateToHTML(contentState);

            // Sende HTML-Inhalt an die API
            await createThread(categoryId, title, description, htmlContent, userId);
            navigate(`/community/${categoryName}`);
        } catch (err) {
            setError(err.message || "Fehler beim Erstellen des Threads.");
        } finally {
            setLoading(false);
        }
    };

    const handleEditorChange = (state) => {
        setEditorState(state); // Aktualisiere den Editor-Zustand bei Änderungen
    };

    return (
        <>
            <main>
                <div className="wrapper">
                    <div className='rows'>
                        <div className='row-forum'>
                            <div className='category'>Community</div>
                            <div className='breadcrumb'>
                                <ul>
                                    <li><Link to={`/community`} viewTransition>Community</Link></li>
                                    <li>/</li>
                                    <li><Link to={`/community/${categoryName}`} viewTransition>{categoryName}</Link></li>
                                    <li>/</li>
                                    <li>Thema erstellen</li>
                                </ul>
                            </div>

                            {error && <div className="error-message">{error}</div>}
                            <form onSubmit={handleSubmit}>
                                <label>
                                    Titel:
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        required
                                    />
                                </label>

                                <label>
                                    Beschreibung:
                                    <input
                                        type="text"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        required
                                    />
                                </label>

                                {/* WYSIWYG-Editor */}
                                <label>
                                    Inhalt:
                                    <div className="editor" style={{ border: '1px solid #ccc', minHeight: '300px', padding: '10px', borderRadius: '0.25rem' }}>
                                        <Editor
                                            editorState={editorState}
                                            onEditorStateChange={handleEditorChange} // Update editor state bei Änderung
                                            toolbarClassName="toolbarClassName"
                                            wrapperClassName="wrapperClassName"
                                            editorClassName="editorClassName"
                                            toolbar={{
                                                options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'colorPicker', 'link', 'embedded', 'emoji', 'image', 'remove', 'history'],
                                                inline: {
                                                    inDropdown: false,
                                                    options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace', 'superscript', 'subscript'],
                                                    bold: { icon: bold },
                                                    italic: { icon: italic },
                                                    underline: { icon: underline },
                                                    strikethrough: { icon: strikethrough },
                                                    monospace: { icon: monospace },
                                                    superscript: { icon: superscript },
                                                    subscript: { icon: subscript },
                                                },
                                                blockType: {
                                                    inDropdown: true,
                                                    options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote', 'Code'],
                                                },
                                                fontSize: {
                                                    icon: fontSize,
                                                    options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
                                                },
                                                fontFamily: {
                                                    options: ['Arial', 'Georgia', 'Impact', 'Tahoma', 'Times New Roman', 'Verdana'],
                                                },
                                                list: {
                                                    inDropdown: false,
                                                    options: ['unordered', 'ordered', 'indent', 'outdent'],
                                                    unordered: { icon: unordered },
                                                    ordered: { icon: ordered },
                                                    indent: { icon: indent },
                                                    outdent: { icon: outdent },
                                                },
                                                textAlign: {
                                                    inDropdown: false,
                                                    options: ['left', 'center', 'right', 'justify'],
                                                    left: { icon: left },
                                                    center: { icon: center },
                                                    right: { icon: right },
                                                    justify: { icon: justify },
                                                },
                                                colorPicker: {
                                                    icon: color,
                                                    colors: [
                                                        'rgb(97,189,109)', 'rgb(26,188,156)', 'rgb(84,172,210)', 'rgb(44,130,201)',
                                                        'rgb(147,101,184)', 'rgb(71,85,119)', 'rgb(204,204,204)', 'rgb(65,168,95)',
                                                        'rgb(0,168,133)', 'rgb(61,142,185)', 'rgb(41,105,176)', 'rgb(85,57,130)', 'rgb(40,50,78)',
                                                        'rgb(0,0,0)', 'rgb(247,218,100)', 'rgb(251,160,38)', 'rgb(235,107,86)', 'rgb(226,80,65)',
                                                        'rgb(163,143,132)', 'rgb(239,239,239)', 'rgb(255,255,255)', 'rgb(250,197,28)', 'rgb(243,121,52)',
                                                        'rgb(209,72,65)', 'rgb(184,49,47)', 'rgb(124,112,107)', 'rgb(209,213,216)'
                                                    ],
                                                },
                                                link: {
                                                    inDropdown: false,
                                                    showOpenOptionOnHover: true,
                                                    defaultTargetOption: '_self',
                                                    options: ['link', 'unlink'],
                                                    link: { icon: link },
                                                    unlink: { icon: unlink },
                                                },
                                                emoji: {
                                                    icon: emoji,
                                                    emojis: [
                                                        '😀', '😁', '😂', '😃', '😉', '😋', '😎', '😍', '😗', '🤗', '🤔', '😣', '😫', '😴', '😌', '🤓',
                                                        '😛', '😜', '😠', '😇', '😷', '😈', '👻', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '🙈',
                                                        '🙉', '🙊', '👼', '👮', '🕵', '💂', '👳', '🎅', '👸', '👰', '👲', '🙍', '🙇', '🚶', '🏃', '💃',
                                                        '⛷', '🏂', '🏌', '🏄', '🚣', '🏊', '⛹', '🏋', '🚴', '👫', '💪', '👈', '👉', '👉', '👆', '🖕',
                                                        '👇', '🖖', '🤘', '🖐', '👌', '👍', '👎', '✊', '👊', '👏', '🙌', '🙏', '🐵', '🐶', '🐇', '🐥',
                                                        '🐸', '🐌', '🐛', '🐜', '🐝', '🍉', '🍄', '🍔', '🍤', '🍨', '🍪', '🎂', '🍰', '🍾', '🍷', '🍸',
                                                        '🍺', '🌍', '🚑', '⏰', '🌙', '🌝', '🌞', '⭐', '🌟', '🌠', '🌨', '🌩', '⛄', '🔥', '🎄', '🎈',
                                                        '🎉', '🎊', '🎁', '🎗', '🏀', '🏈', '🎲', '🔇', '🔈', '📣', '🔔', '🎵', '🎷', '💰', '🖊', '📅',
                                                        '✅', '❎', '💯',
                                                    ],
                                                },
                                                embedded: {
                                                    icon: embedded,
                                                },
                                                image: {
                                                    icon: image,
                                                    urlEnabled: true,
                                                    uploadEnabled: true,
                                                    alignmentEnabled: true,
                                                    previewImage: false,
                                                    inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg',
                                                },
                                                remove: { icon: eraser },
                                                history: {
                                                    inDropdown: false,
                                                    options: ['undo', 'redo'],
                                                    undo: { icon: undo },
                                                    redo: { icon: redo },
                                                },
                                            }}
                                        />
                                    </div>
                                </label>

                                <button type="submit" disabled={loading}>
                                    {loading ? 'Laden...' : 'Thema erstellen'}
                                </button>
                            </form>
                        </div>
                        <div className='row'>
                            <Tests />
                            <News />
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default CreateThread;